import * as PIXI from "pixi.js";
import { queueMeshWarm } from "./spineMeshWarm";

/**
 * spineDormancy — a HIDDEN room's spines, asleep (2026-10-08, Vladimir: "we
 * can have 3 gameviews at same time this all must work flawlessly").
 *
 * The three rooms (base, free spins, Hold & Win) are always mounted and the
 * ones not in play sit at alpha 0.001 — never `visible = false`: a subtree
 * that is not drawn lets PIXI's texture GC unload its atlas, and the frame it
 * comes back on pays for all of it (the Mad Labs transition hitch).
 *
 * But a spine at alpha 0.001 still did ALL of its work every frame: its
 * AnimationState, the world transform of every bone, and renderMeshes() —
 * which runs in updateTransform whether or not autoUpdate is on — rebuilding
 * every slot's vertices. Clouds, fires, plates, the logo: two whole rooms of
 * it, for nothing anyone could see.
 *
 * Asleep, per frame:
 *   kept     AnimationState.update + apply — the clock runs and the timelines
 *            land as before: queued clips start on time, complete / end and
 *            timeline events still fire (a logo stage set on the hidden room
 *            is where it should be when the room comes back);
 *   skipped  the world transform, physics and the vertex rebuild. The LAST
 *            meshes are still drawn at the room's 0.001 — the atlas stays
 *            bound, so the GC never takes it.
 * Woken, the next frame is one ordinary full update: the pose is exactly
 * where it would have been had it never slept.
 *
 * A spine that was never drawn (a room hidden from the start) has its meshes
 * built once, for every clip, when it first goes to sleep (spineMeshWarm) —
 * otherwise its first frame awake, at the wipe's midpoint, would build them.
 *
 * Only a room's OWN spines are put to sleep (each controller names them): the
 * boards inside it run their overlay spines' autoUpdate themselves.
 */

const asleep = new Map(); // spine -> { advance: boolean }
let ticking = false;

// The sleeping spine's frame: its children still update (slot objects keep
// their own transforms), but no renderMeshes — the last meshes stand.
function sleepingUpdateTransform() {
  this.sortChildren();
  PIXI.Container.prototype.updateTransform.call(this);
}

const tick = () => {
  const delta = PIXI.Ticker.shared.deltaMS / 1000;
  for (const [spine, { advance }] of asleep) {
    if (spine.destroyed || !spine.state || !spine.skeleton) {
      asleep.delete(spine);
      continue;
    }
    // Only a spine that was running on its own: one driven by hand keeps
    // being driven by hand.
    if (!advance) continue;
    try {
      spine.state.update(delta);
      spine.state.apply(spine.skeleton);
    } catch {
      // A sleeping room must never stop the game.
    }
  }
  if (!asleep.size) stop();
};

const start = () => {
  if (ticking) return;
  ticking = true;
  PIXI.Ticker.shared.add(tick);
};

function stop() {
  if (!ticking) return;
  ticking = false;
  PIXI.Ticker.shared.remove(tick);
}

/** Put one spine to sleep. Safe to repeat. */
export const sleepSpine = (spine) => {
  if (!spine || spine.destroyed || !spine.state || asleep.has(spine)) return;
  // Never drawn yet: build its meshes once, for every clip, before it sleeps.
  if (spine.hasNeverUpdated && !spine.__dormancyWarmed) {
    spine.__dormancyWarmed = true;
    queueMeshWarm({ spine });
  }
  const advance = spine.autoUpdate === true;
  spine.autoUpdate = false;
  spine.updateTransform = sleepingUpdateTransform;
  asleep.set(spine, { advance });
  start();
};

/** Wake one spine — the next frame is a full update. Safe to repeat. */
export const wakeSpine = (spine) => {
  if (!spine) return;
  const entry = asleep.get(spine);
  if (!entry) return;
  asleep.delete(spine);
  // Back to Spine.prototype.updateTransform (renderMeshes + children).
  delete spine.updateTransform;
  if (!spine.destroyed && entry.advance) {
    spine.autoUpdate = true;
    // Posed NOW, not on the next tick: whatever reads the room in this same
    // frame (an entry sequence at the wipe's midpoint, a slot's bone sync in
    // afterUpdateWorldTransforms, a layout changed by a resize while it slept)
    // sees the pose it would have had. Zero delta — the clock already ran.
    try {
      spine.update(0);
    } catch {
      // ignore — the next tick updates it anyway
    }
  }
  if (!asleep.size) stop();
};

/** Sleep or wake a list (nulls skipped). */
export const setSpinesDormant = (spines, dormant) => {
  for (const spine of spines) {
    if (dormant) sleepSpine(spine);
    else wakeSpine(spine);
  }
};
